/*
 * Cart shipping-tier signal
 * -------------------------
 * Keeps a single cart attribute (`_shipping_tier`) in sync with the cart's total
 * weight so downstream consumers can react to "heavy" (≈ 3+ pairs) baskets:
 *   - Ingrid filters on `cart.attributes._shipping_tier == 'heavy'` to apply the
 *     DE heavy-cart shipping fee.
 *   - The checkout block shows the "why shipping isn't free" notice on the same flag.
 *
 * The flag is market-agnostic — it only says "this basket is at/over the weight
 * threshold". Market gating (which markets actually charge / show the notice) lives
 * in Ingrid and the checkout block, so non-DE rollout needs no theme change.
 *
 * Threshold comes from window.SHIPPING_TIER_THRESHOLD_G (theme setting), default 4500 g.
 */
(function () {
  'use strict';

  var ATTR_KEY = '_shipping_tier';
  var HEAVY = 'heavy';

  function threshold() {
    var t = window.SHIPPING_TIER_THRESHOLD_G;
    return typeof t === 'number' && t > 0 ? t : 4500;
  }

  // Weight only changes when cart *contents* change: /cart/add and /cart/change
  // (qty + removals). It never changes on /cart/update (note + attributes), so we
  // deliberately do NOT react to /cart/update — that is also the endpoint our own
  // write below uses, which is how we avoid a write-loop.
  var CART_MUTATION = /\/cart\/(add|change|clear)(\.js)?($|\?)/;

  var inFlight = false;
  var pending = false;

  function syncTier() {
    // Coalesce rapid triggers: run one sync at a time, with at most one queued rerun
    // so the final cart state is always reconciled.
    if (inFlight) {
      pending = true;
      return;
    }
    inFlight = true;

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (cart) {
        if (!cart) return null;
        var current = (cart.attributes && cart.attributes[ATTR_KEY]) || '';
        var desired = (cart.total_weight || 0) >= threshold() ? HEAVY : '';
        if (current === desired) return null; // idempotent: only write on change

        var attributes = {};
        attributes[ATTR_KEY] = desired; // '' removes the attribute
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: attributes }),
        });
      })
      .catch(function () {
        // Transient/network error — the next cart change or page load resyncs.
      })
      .then(function () {
        inFlight = false;
        if (pending) {
          pending = false;
          syncTier();
        }
      });
  }

  // Reconcile on every page load (existing baskets, return visits to /cart, etc.).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncTier);
  } else {
    syncTier();
  }

  // Reconcile after any content mutation. Wrapping fetch is a pure pass-through
  // observer: it returns the original promise untouched and never reads the body.
  // The window guard prevents double-wrapping if the script is included twice.
  if (window.fetch && !window.__shippingTierFetchWrapped) {
    window.__shippingTierFetchWrapped = true;
    var nativeFetch = window.fetch;
    window.fetch = function (input) {
      var promise = nativeFetch.apply(this, arguments);
      try {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        if (CART_MUTATION.test(url)) {
          promise
            .then(function (res) {
              if (res && res.ok) syncTier();
            })
            .catch(function () {});
        }
      } catch (e) {
        // Never let observation interfere with the caller's request.
      }
      return promise;
    };
  }
})();
